import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Maximize2, Minimize2, Loader2 } from 'lucide-react';
import { subscribeToGallery } from '../services/firebase';
import { subscribeToCategories } from '../services/firestore';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [gallery, setGallery] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [visibleCount, setVisibleCount] = useState(8);
  const TAMBAH_PER_KALI = 8;

  const touchStartRef = useRef(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Subscribe to gallery data
  useEffect(() => {
    const unsubscribeGallery = subscribeToGallery((data) => {
      setGallery(data);
      setLoading(false);
    });

    const unsubscribeCategories = subscribeToCategories((data) => {
      setCategories(data);
    });

    return () => {
      unsubscribeGallery();
      unsubscribeCategories();
    };
  }, []);

  const filteredGallery = filter === 'all'
    ? gallery
    : gallery.filter(item => item.categoryId === filter || item.category === filter);

  const navigateImage = useCallback((direction) => {
    setIsLoading(true);
    let newIndex;

    if (direction === 'next') {
      newIndex = (imageIndex + 1) % filteredGallery.length;
    } else {
      newIndex = (imageIndex - 1 + filteredGallery.length) % filteredGallery.length;
    }

    setImageIndex(newIndex);
    setSelectedImage(filteredGallery[newIndex]);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, [imageIndex, filteredGallery]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          navigateImage('prev');
          break;
        case 'ArrowRight':
          e.preventDefault();
          navigateImage('next');
          break;
        case 'Escape':
          e.preventDefault();
          closeLightbox();
          break;
        case 'f':
        case 'F':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            toggleFullscreen();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, imageIndex, navigateImage]);

  const openLightbox = (image, index) => {
    const actualIndex = filteredGallery.findIndex(img => img.id === image.id);
    setImageIndex(actualIndex);
    setSelectedImage(image);
    setIsLoading(true);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    setImageIndex(0);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
    setIsFullscreen(false);
    document.body.style.overflow = 'auto';
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Touch handling for swipe gestures
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
  };

  const handleTouchMove = (e) => {
    if (!touchStartRef.current || zoomLevel > 1) return;

    const touch = e.touches[0];
    const diffX = touch.clientX - touchStartRef.current.x;
    const diffY = touch.clientY - touchStartRef.current.y;

    // Detect horizontal swipe
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (diffX > 0) {
        navigateImage('prev');
      } else {
        navigateImage('next');
      }
      touchStartRef.current = null;
    }
  };

  // Pinch to zoom
  const handleWheel = (e) => {
    if (!selectedImage) return;

    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.min(Math.max(zoomLevel + delta, 1), 3);
    setZoomLevel(newZoom);

    if (newZoom === 1) {
      setPosition({ x: 0, y: 0 });
    }
  };

  // Handle mouse drag for pan
  const handleMouseDown = (e) => {
    if (zoomLevel <= 1) return;

    const startX = e.clientX - position.x;
    const startY = e.clientY - position.y;

    const handleMouseMove = (moveEvent) => {
      setPosition({
        x: moveEvent.clientX - startX,
        y: moveEvent.clientY - startY,
      });
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Reset visible count when filter changes
  useEffect(() => {
    setVisibleCount(8);
  }, [filter]);

  const handleClickOutside = (e) => {
    if (e.target === e.currentTarget) {
      closeLightbox();
    }
  };

  return (
    <section id="gallery" className="py-16 xs:py-20 sm:py-24 md:py-28 lg:py-32 bg-black relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-1/3 right-0 w-64 h-64 xs:w-80 xs:h-80 md:w-96 md:h-96 bg-[#D4AF7A]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/3 left-0 w-64 h-64 xs:w-80 xs:h-80 md:w-96 md:h-96 bg-[#C9A882]/5 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 xs:px-6 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 xs:mb-12 md:mb-16">
          <span className="inline-block px-3 xs:px-4 py-1.5 xs:py-2 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-full text-[#D4AF7A] text-xs xs:text-sm font-semibold mb-3 xs:mb-4">
            Portfolio
          </span>
          <h2 className="text-2xl xs:text-3xl md:text-4xl lg:text-5xl font-bold text-[#F7F5F2] mb-4 xs:mb-6">
            Galeri Karya Kami
          </h2>
          <div className="w-16 xs:w-20 md:w-24 h-1 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] mx-auto mb-4 xs:mb-6 md:mb-8"></div>
          <p className="text-xs xs:text-sm md:text-base lg:text-lg text-[#F7F5F2]/70 max-w-2xl mx-auto px-2">
            Koleksi design henna terbaik untuk berbagai momen spesial Anda
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-16 xs:py-20">
            <Loader2 className="w-10 h-10 xs:w-12 xs:h-12 animate-spin text-[#D4AF7A]" aria-hidden="true" />
            <span className="sr-only">Memuat galeri...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-8 text-center" role="alert">
            {error}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && gallery.length === 0 && (
          <div className="text-center py-16 xs:py-20">
            <p className="text-[#F7F5F2]/70 text-sm xs:text-base md:text-lg">Belum ada galeri yang tersedia.</p>
          </div>
        )}

        {/* Content */}
        {!loading && !error && gallery.length > 0 && (
          <>
            {/* Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-2 xs:gap-3 mb-8 xs:mb-12" role="tablist" aria-label="Filter kategori galeri">
              <button
                key="all"
                onClick={() => setFilter('all')}
                className={`px-4 xs:px-5 md:px-6 py-2 xs:py-2.5 min-h-[40px] xs:min-h-[44px] rounded-full text-xs xs:text-sm font-medium transition-all duration-300 capitalize focus:outline-none focus:ring-2 focus:ring-[#D4AF7A] ${
                  filter === 'all'
                    ? 'bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black shadow-lg shadow-[#D4AF7A]/30'
                    : 'bg-white/5 text-[#F7F5F2] border border-[#D4AF7A]/30 hover:bg-white/10'
                }`}
                role="tab"
                aria-selected={filter === 'all'}
                aria-label="Tampilkan semua galeri"
              >
                Semua
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setFilter(cat.id)}
                  className={`px-4 xs:px-5 md:px-6 py-2 xs:py-2.5 min-h-[40px] xs:min-h-[44px] rounded-full text-xs xs:text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A] ${
                    filter === cat.id
                      ? 'bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black shadow-lg shadow-[#D4AF7A]/30'
                      : 'bg-white/5 text-[#F7F5F2] border border-[#D4AF7A]/30 hover:bg-white/10'
                  }`}
                  role="tab"
                  aria-selected={filter === cat.id}
                  aria-label={`Filter kategori ${cat.name}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Gallery Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 xs:gap-4 md:gap-6">
              {filteredGallery.slice(0, visibleCount).map((item, index) => (
                <div
                  key={item.id}
                  className="group relative aspect-square overflow-hidden rounded-lg xs:rounded-xl cursor-pointer focus-within:ring-2 focus-within:ring-[#D4AF7A] animate-fade-in-up"
                  onClick={() => openLightbox(item, index)}
                  style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'both' }}
                >
                  {/* Image */}
                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-sm md:text-base text-[#F7F5F2] font-semibold mb-1">{item.title}</h3>
                      <p className="text-[#D4AF7A] text-xs md:text-sm capitalize">
                        {item.categoryName || item.category}
                      </p>
                    </div>
                  </div>

                  {/* Border Effect */}
                  <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#D4AF7A]/50 rounded-xl transition-all duration-300"></div>

                  {/* Zoom Icon Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Maximize2 className="w-12 h-12 text-white/80" />
                  </div>
                </div>
              ))}
            </div>

            {/* Show More Button */}
            {visibleCount < filteredGallery.length && (
              <div className="text-center mt-8 xs:mt-10 md:mt-12">
                <button
                  onClick={() => setVisibleCount((prev) => prev + TAMBAH_PER_KALI)}
                  className="px-6 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-full hover:shadow-lg hover:shadow-[#D4AF7A]/50 transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]"
                >
                  Tampilkan Lainnya ({filteredGallery.length - visibleCount} tersisa)
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Enhanced Lightbox */}
      {selectedImage && (
        <div
          ref={containerRef}
          className="fixed inset-0 z-50 flex flex-col bg-black/95"
          onClick={handleClickOutside}
          role="dialog"
          aria-modal="true"
          aria-label={`Lightbox gambar ${selectedImage.title}, ${imageIndex + 1} dari ${filteredGallery.length}`}
        >
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between p-3 xs:p-4 sm:p-6 border-b border-white/10">
            <div className="flex items-center gap-2 xs:gap-4 min-w-0">
              <span className="text-[#F7F5F2]/80 text-xs xs:text-sm whitespace-nowrap">
                {imageIndex + 1} / {filteredGallery.length}
              </span>
              <h3 className="text-[#F7F5F2] font-semibold text-sm xs:text-base hidden sm:block truncate max-w-[120px] xs:max-w-md">
                {selectedImage.title}
              </h3>
            </div>
            <div className="flex items-center gap-1.5 xs:gap-2">
              {/* Zoom Controls */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setZoomLevel(Math.max(1, zoomLevel - 0.25));
                  if (zoomLevel - 0.25 <= 1) setPosition({ x: 0, y: 0 });
                }}
                className="p-2 min-w-[36px] min-h-[36px] xs:min-w-[40px] xs:min-h-[40px] flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg text-[#F7F5F2] transition-all disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]"
                disabled={zoomLevel <= 1}
                aria-label="Perkecil tampilan"
              >
                <span className="text-sm xs:text-base font-bold">-</span>
              </button>
              <span className="text-[#F7F5F2]/80 text-xs xs:text-sm w-10 xs:w-12 text-center">
                {Math.round(zoomLevel * 100)}%
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setZoomLevel(Math.min(3, zoomLevel + 0.25));
                }}
                className="p-2 min-w-[36px] min-h-[36px] xs:min-w-[40px] xs:min-h-[40px] flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg text-[#F7F5F2] transition-all disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]"
                disabled={zoomLevel >= 3}
                aria-label="Perbesar tampilan"
              >
                <span className="text-sm xs:text-base font-bold">+</span>
              </button>
              {/* Fullscreen Toggle */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFullscreen();
                }}
                className="p-2 min-w-[36px] min-h-[36px] xs:min-w-[40px] xs:min-h-[40px] flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg text-[#F7F5F2] transition-all focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]"
                aria-label="Layar penuh"
              >
                {isFullscreen ? <Minimize2 size={18} className="w-5 h-5 w-5 h-5 xs:w-5 xs:h-5" /> : <Maximize2 size={18} className="w-5 h-5 w-5 h-5 xs:w-5 xs:h-5" />}
              </button>
              {/* Close Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeLightbox();
                }}
                className="p-2 min-w-[36px] min-h-[36px] xs:min-w-[40px] xs:min-h-[40px] flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg text-[#F7F5F2] transition-all focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]"
                aria-label="Tutup lightbox (ESC)"
              >
                <X size={18} className="w-5 h-5 xs:w-5 xs:h-5" />
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex items-center justify-center relative overflow-hidden px-4">
            {/* Loading Spinner */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <Loader2 className="w-10 h-10 xs:w-12 xs:h-12 animate-spin text-[#D4AF7A]" aria-hidden="true" />
                <span className="sr-only">Memuat gambar...</span>
              </div>
            )}

            {/* Previous Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateImage('prev');
              }}
              className="absolute left-2 xs:left-4 sm:left-6 p-2 xs:p-3 min-w-[40px] min-h-[40px] xs:min-w-[48px] xs:min-h-[48px] bg-white/10 hover:bg-white/20 rounded-full text-[#F7F5F2] transition-all duration-300 z-20 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]"
              aria-label="Gambar sebelumnya"
            >
              <ChevronLeft size={24} className="w-7 h-7 xs:w-7 xs:h-7" />
            </button>

            {/* Image Container */}
            <div
              className="relative overflow-hidden touch-pan-y"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onWheel={handleWheel}
            >
              <img
                ref={imageRef}
                src={selectedImage.image}
                alt={selectedImage.title}
                onLoad={handleImageLoad}
                onMouseDown={handleMouseDown}
                style={{
                  transform: `scale(${zoomLevel}) translate(${position.x}px, ${position.y}px)`,
                  transition: zoomLevel > 1 ? 'transform 0.1s ease-out' : 'transform 0.3s ease-out',
                  cursor: zoomLevel > 1 ? 'grab' : 'default',
                  maxHeight: '60vh xs:max-h-[65vh] sm:max-h-[70vh]',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
                className="rounded-lg"
                draggable={false}
              />
            </div>

            {/* Next Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateImage('next');
              }}
              className="absolute right-2 xs:right-4 sm:right-6 p-2 xs:p-3 min-w-[40px] min-h-[40px] xs:min-w-[48px] xs:min-h-[48px] bg-white/10 hover:bg-white/20 rounded-full text-[#F7F5F2] transition-all duration-300 z-20 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]"
              aria-label="Gambar selanjutnya"
            >
              <ChevronRight size={24} className="w-7 h-7 xs:w-7 xs:h-7" />
            </button>
          </div>

          {/* Footer - Thumbnails & Info */}
          <div className="flex-shrink-0 p-3 xs:p-4 border-t border-white/10">
            {/* Thumbnail Strip */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide touch-pan-x">
              {filteredGallery.map((item, idx) => (
                <button
                  key={item.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (idx !== imageIndex) {
                      setImageIndex(idx);
                      setSelectedImage(item);
                      setIsLoading(true);
                    }
                  }}
                  className={`flex-shrink-0 w-12 h-12 xs:w-14 xs:h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all focus:outline-none focus:ring-2 focus:ring-[#D4AF7A] ${
                    idx === imageIndex
                      ? 'border-[#D4AF7A] opacity-100'
                      : 'border-transparent opacity-50 hover:opacity-80'
                  }`}
                  aria-label={`Tampilkan gambar ${item.title}`}
                  aria-pressed={idx === imageIndex}
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>

            {/* Image Info */}
            <div className="text-center mt-2 xs:mt-2">
              <h3 className="text-sm xs:text-base sm:text-lg font-bold text-[#F7F5F2] mb-1 sm:hidden">
                {selectedImage.title}
              </h3>
              <div className="flex items-center justify-center gap-2 xs:gap-4 text-xs xs:text-sm">
                <span className="text-[#D4AF7A]">
                  {selectedImage.categoryName || selectedImage.category}
                </span>
                {selectedImage.description && (
                  <span className="text-[#F7F5F2]/60">{selectedImage.description}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
